import React, { useMemo } from "react";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Contacts, Time_Entries } from "@/src/gql/graphql";
import { useTimeEntries } from "@/lib/hooks/api/time-entries";
import { useTimeEntryHooks } from "@/lib/hooks/api/time-entries/time-entry-hooks";
import { SimpleTable } from "@/components/shared/UniversalCRMTable/SimpleTable";
import { getEntityTableConfig } from "@/lib/entity-registry";

const TIME_ENTRY_CONFIG = getEntityTableConfig<Time_Entries>("time_entries");
import { ColumnManagerProvider } from "@/lib/column-manager";
import { createColumnsFromConfig } from "@/lib/utils/columnFactory";
import { ContextualTableFilter } from "@/components/shared/tables/ContextualTableFilter";
import { FilterContextProvider } from "@/contexts/FilterContext";
import { useContextualTableFilter } from "@/lib/hooks/search/useContextualTableFilter";

/**
 * Contact Time Entries Tab
 *
 * Shows all time entries linked to the contact.
 * Supabase returns an enriched shape; we bridge it to Time_Entries for SimpleTable.
 */
export const ContactTimeEntriesDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
}) => {
  const {
    data: timeEntries = [],
    isLoading,
    error,
  } = useTimeEntries({ contactId: contact.id });

  const { contextFilterLabel, isContextFilterActive, clearContextFilter } =
    useContextualTableFilter("time_entries");

  const defaultColumns = useMemo(() => createColumnsFromConfig(TIME_ENTRY_CONFIG), []);

  // useTimeEntries returns Supabase rows enriched with joined fields; we cast to
  // Time_Entries[] here because SimpleTable only reads the columns declared in
  // TIME_ENTRY_CONFIG, all of which exist on the Supabase row shape.
  const tableData = timeEntries as unknown as Time_Entries[];

  return (
    <div className="mt-4 space-y-4">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-card-foreground break-words md:break-normal">
          Time Entries for {contact.name}
        </h3>
        <p className="text-sm text-gray-600 mt-1 dark:text-gray-400">
          View all time entries associated with this contact.
        </p>
      </div>

      <FilterContextProvider<Time_Entries> config={TIME_ENTRY_CONFIG} entityName="time_entries">
        <ColumnManagerProvider
          storageKey={`contact-${contact.id}-time-entries-column-preferences`}
          defaultColumns={defaultColumns}
        >
          <ContextualTableFilter
            label={contextFilterLabel}
            isActive={isContextFilterActive}
            onClear={clearContextFilter}
          />
          <SimpleTable<Time_Entries>
            config={TIME_ENTRY_CONFIG}
            data={tableData}
            isLoading={isLoading}
            error={error}
            isDataPreFiltered={true}
            hooks={useTimeEntryHooks()}
          />
        </ColumnManagerProvider>
      </FilterContextProvider>
    </div>
  );
};
